import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    FileText, 
    Plus, 
    ClipboardList,
    FileCheck,
    Clock,
    ChevronRight,
    Trash2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORM_TYPES = [
    { value: 'inspection', label: 'Inspection', icon: ClipboardList },
    { value: 'assessment', label: 'Assessment', icon: FileCheck },
    { value: 'checklist', label: 'Checklist', icon: FileText },
];

const FIELD_TYPES = [
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Long Text' },
    { value: 'number', label: 'Number' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'select', label: 'Dropdown' },
    { value: 'date', label: 'Date' },
];

const FormCard = ({ template, onFill, onDelete, canDelete }) => {
    const TypeIcon = FORM_TYPES.find(t => t.value === template.form_type)?.icon || FileText;
    
    return (
        <Card className="hover-lift cursor-pointer group" onClick={() => onFill(template)}>
            <CardContent className="p-4">
                <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                        <div className="p-3 rounded-lg bg-primary/10">
                            <TypeIcon className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h3 className="font-semibold group-hover:text-primary transition-colors">
                                {template.name}
                            </h3>
                            {template.description && (
                                <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
                            )}
                            <div className="flex items-center gap-3 mt-2">
                                <Badge variant="outline" className="capitalize">
                                    {template.form_type}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                    {template.fields?.length || 0} fields
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {canDelete && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="opacity-0 group-hover:opacity-100 text-destructive"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(template.id);
                                }}
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        )}
                        <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

const SubmissionCard = ({ submission }) => (
    <Card>
        <CardContent className="p-4">
            <div className="flex items-start justify-between">
                <div>
                    <h4 className="font-medium">{submission.template_name}</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                        Submitted by {submission.submitted_by_name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                        {format(new Date(submission.created_at), 'MMM d, yyyy h:mm a')}
                    </p>
                </div>
                <Badge variant="secondary">
                    <FileCheck className="w-3 h-3 mr-1" />
                    Submitted
                </Badge>
            </div>
        </CardContent>
    </Card>
);

export default function FormsPage() {
    const { hasUnitAccess } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showFillDialog, setShowFillDialog] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [formValues, setFormValues] = useState({});
    
    const [newTemplate, setNewTemplate] = useState({
        name: '',
        description: '',
        form_type: 'checklist',
        fields: []
    });
    
    const [newField, setNewField] = useState({
        name: '',
        type: 'text',
        required: false,
        options: ''
    });
    
    useEffect(() => {
        fetchData();
    }, []);
    
    const fetchData = async () => {
        try {
            const [templatesRes, submissionsRes] = await Promise.all([
                axios.get(`${API_URL}/forms/templates`),
                axios.get(`${API_URL}/forms/submissions`)
            ]);
            setTemplates(templatesRes.data);
            setSubmissions(submissionsRes.data);
        } catch (error) {
            console.error('Failed to fetch forms:', error);
            toast.error('Failed to load forms');
        } finally {
            setLoading(false);
        }
    };
    
    const handleAddField = () => {
        if (!newField.name) {
            toast.error('Field name is required');
            return;
        }
        
        const field = {
            ...newField,
            options: newField.type === 'select' ? newField.options.split(',').map(o => o.trim()) : undefined
        };
        
        setNewTemplate({
            ...newTemplate,
            fields: [...newTemplate.fields, field]
        });
        
        setNewField({ name: '', type: 'text', required: false, options: '' });
    };
    
    const handleRemoveField = (index) => {
        setNewTemplate({
            ...newTemplate,
            fields: newTemplate.fields.filter((_, i) => i !== index)
        });
    };
    
    const handleCreateTemplate = async (e) => {
        e.preventDefault();
        if (newTemplate.fields.length === 0) {
            toast.error('Add at least one field');
            return;
        }
        
        try {
            await axios.post(`${API_URL}/forms/templates`, newTemplate);
            toast.success('Form template created');
            setShowCreateDialog(false);
            setNewTemplate({ name: '', description: '', form_type: 'checklist', fields: [] });
            fetchData();
        } catch (error) {
            toast.error('Failed to create template');
        }
    };
    
    const handleFillForm = (template) => {
        setSelectedTemplate(template);
        setFormValues({});
        setShowFillDialog(true);
    };
    
    const handleSubmitForm = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/forms/submissions`, {
                template_id: selectedTemplate.id,
                data: formValues
            });
            toast.success('Form submitted');
            setShowFillDialog(false);
            setSelectedTemplate(null);
            setFormValues({});
            fetchData();
        } catch (error) {
            toast.error('Failed to submit form');
        }
    };
    
    const handleDeleteTemplate = async (id) => {
        if (!window.confirm('Delete this form template?')) return;
        try {
            // Note: Would need a delete endpoint
            toast.info('Delete functionality would be implemented here');
        } catch (error) {
            toast.error('Failed to delete template');
        }
    };
    
    const renderFormField = (field, index) => {
        const value = formValues[field.name] || '';
        
        switch (field.type) {
            case 'text':
                return (
                    <Input
                        value={value}
                        onChange={(e) => setFormValues({ ...formValues, [field.name]: e.target.value })}
                        required={field.required}
                    />
                );
            case 'textarea':
                return (
                    <Textarea
                        value={value}
                        onChange={(e) => setFormValues({ ...formValues, [field.name]: e.target.value })}
                        required={field.required}
                    />
                );
            case 'number':
                return (
                    <Input
                        type="number"
                        value={value}
                        onChange={(e) => setFormValues({ ...formValues, [field.name]: e.target.value })}
                        required={field.required}
                    />
                );
            case 'checkbox':
                return (
                    <div className="flex items-center gap-2">
                        <Checkbox
                            checked={!!value}
                            onCheckedChange={(checked) => setFormValues({ ...formValues, [field.name]: checked })}
                        />
                        <span className="text-sm">{field.required ? 'Required' : 'Optional'}</span>
                    </div>
                );
            case 'select':
                return (
                    <Select
                        value={value}
                        onValueChange={(val) => setFormValues({ ...formValues, [field.name]: val })}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Select option" />
                        </SelectTrigger>
                        <SelectContent>
                            {field.options?.map((option) => (
                                <SelectItem key={option} value={option}>
                                    {option}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                );
            case 'date':
                return (
                    <Input
                        type="date"
                        value={value}
                        onChange={(e) => setFormValues({ ...formValues, [field.name]: e.target.value })}
                        required={field.required}
                    />
                );
            default:
                return null;
        }
    };
    
    if (loading) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <Card key={i}>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-4">
                                    <div className="skeleton w-12 h-12 rounded-lg"></div>
                                    <div className="flex-1">
                                        <div className="skeleton h-5 w-32 mb-2 rounded"></div>
                                        <div className="skeleton h-4 w-48 rounded"></div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        );
    }
    
    return (
        <div className="space-y-6 animate-fade-in" data-testid="forms-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold">Forms</h2>
                    <p className="text-muted-foreground">
                        Create and fill inspection checklists and assessments
                    </p>
                </div>
                {hasUnitAccess() && (
                    <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                        <DialogTrigger asChild>
                            <Button className="ses-btn-primary gap-2" data-testid="create-form-btn">
                                <Plus className="w-4 h-4" />
                                Create Form
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle>Create Form Template</DialogTitle>
                                <DialogDescription>
                                    Design a new form template for your unit
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleCreateTemplate} className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Form Name</Label>
                                    <Input
                                        value={newTemplate.name}
                                        onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                                        placeholder="e.g., Vehicle Inspection Checklist"
                                        required
                                        data-testid="form-name"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Description</Label>
                                    <Textarea
                                        value={newTemplate.description}
                                        onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                                        placeholder="Describe the purpose of this form"
                                        data-testid="form-description"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Form Type</Label>
                                    <Select
                                        value={newTemplate.form_type}
                                        onValueChange={(value) => setNewTemplate({ ...newTemplate, form_type: value })}
                                    >
                                        <SelectTrigger data-testid="form-type">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {FORM_TYPES.map((type) => (
                                                <SelectItem key={type.value} value={type.value}>
                                                    {type.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                
                                {/* Fields Section */}
                                <div className="border-t pt-4">
                                    <h4 className="font-medium mb-3">Form Fields</h4>
                                    
                                    {newTemplate.fields.length > 0 && (
                                        <div className="space-y-2 mb-4">
                                            {newTemplate.fields.map((field, index) => (
                                                <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                                                    <div>
                                                        <span className="font-medium text-sm">{field.name}</span>
                                                        <span className="text-xs text-muted-foreground ml-2">({field.type})</span>
                                                        {field.required && <Badge variant="outline" className="ml-2 text-xs">Required</Badge>}
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleRemoveField(index)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    
                                    <div className="space-y-3 p-3 border rounded-lg">
                                        <div className="grid grid-cols-2 gap-2">
                                            <Input
                                                value={newField.name}
                                                onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                                                placeholder="Field name"
                                                data-testid="field-name"
                                            />
                                            <Select
                                                value={newField.type}
                                                onValueChange={(value) => setNewField({ ...newField, type: value })}
                                            >
                                                <SelectTrigger data-testid="field-type">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {FIELD_TYPES.map((type) => (
                                                        <SelectItem key={type.value} value={type.value}>
                                                            {type.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        {newField.type === 'select' && (
                                            <Input
                                                value={newField.options}
                                                onChange={(e) => setNewField({ ...newField, options: e.target.value })}
                                                placeholder="Options (comma separated)"
                                                data-testid="field-options"
                                            />
                                        )}
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Checkbox
                                                    checked={newField.required}
                                                    onCheckedChange={(checked) => setNewField({ ...newField, required: checked })}
                                                />
                                                <span className="text-sm">Required</span>
                                            </div>
                                            <Button type="button" variant="secondary" size="sm" onClick={handleAddField} data-testid="add-field-btn">
                                                Add Field
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                                
                                <DialogFooter>
                                    <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" className="ses-btn-primary" data-testid="submit-template">
                                        Create Template
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>
            
            <Tabs defaultValue="templates" className="w-full">
                <TabsList>
                    <TabsTrigger value="templates" data-testid="tab-templates">
                        <FileText className="w-4 h-4 mr-2" />
                        Templates
                    </TabsTrigger>
                    <TabsTrigger value="submissions" data-testid="tab-submissions">
                        <Clock className="w-4 h-4 mr-2" />
                        Submissions
                    </TabsTrigger>
                </TabsList>
                
                <TabsContent value="templates" className="mt-6">
                    {templates.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {templates.map((template) => (
                                <FormCard
                                    key={template.id}
                                    template={template}
                                    onFill={handleFillForm}
                                    onDelete={handleDeleteTemplate}
                                    canDelete={hasUnitAccess()}
                                />
                            ))}
                        </div>
                    ) : (
                        <Card>
                            <CardContent className="py-12 text-center">
                                <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                                <h3 className="font-medium text-lg">No form templates</h3>
                                <p className="text-muted-foreground mt-1">
                                    Create your first form template to get started
                                </p>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>
                
                <TabsContent value="submissions" className="mt-6">
                    {submissions.length > 0 ? (
                        <div className="space-y-3">
                            {submissions.map((submission) => (
                                <SubmissionCard key={submission.id} submission={submission} />
                            ))}
                        </div>
                    ) : (
                        <Card>
                            <CardContent className="py-12 text-center">
                                <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                                <h3 className="font-medium text-lg">No submissions yet</h3>
                                <p className="text-muted-foreground mt-1">
                                    Fill out a form to see submissions here
                                </p>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>
            </Tabs>
            
            {/* Fill Form Dialog */}
            <Dialog open={showFillDialog} onOpenChange={setShowFillDialog}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{selectedTemplate?.name}</DialogTitle>
                        <DialogDescription>
                            {selectedTemplate?.description || 'Fill out the form below'}
                        </DialogDescription>
                    </DialogHeader>
                    {selectedTemplate && (
                        <form onSubmit={handleSubmitForm} className="space-y-4">
                            {selectedTemplate.fields?.map((field, index) => (
                                <div key={index} className="space-y-2">
                                    <Label>
                                        {field.name}
                                        {field.required && <span className="text-destructive ml-1">*</span>}
                                    </Label>
                                    {renderFormField(field, index)}
                                </div>
                            ))}
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setShowFillDialog(false)}>
                                    Cancel
                                </Button>
                                <Button type="submit" className="ses-btn-primary" data-testid="submit-form">
                                    Submit Form
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
